import { useState, useEffect, useCallback } from 'react';
import { roleApi } from '../../api/roleApi';
import { Role } from '../../api/roleApi';
import { menuApi } from '../../api/menuApi';
import AddRoleDialog from './AddRoleDialog';
import EditRoleDialog from './EditRoleDialog';
import RolePermissionsDialog from './RolePermissionsDialog';
import { useSnackbar } from 'notistack';

// Extend the Role type with any additional properties needed for the UI
type UIRole = Role & {
  // Add any additional UI-specific properties here if needed
};
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Container,
  Chip,
  Avatar,
  CardHeader,
  CardActions,
  IconButton,
  Tooltip,
  Divider,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Group as GroupIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Lock as LockIcon
} from '@mui/icons-material';

// Mock data for the UI
const MOCK_ROLES: UIRole[] = [
  {
    id: '1',
    name: 'Administrator',
    description: 'Full system access',
    isActive: true,
    isSystem: true,
    userCount: 5,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Doctor',
    description: 'Can manage patient records and appointments',
    isActive: true,
    isSystem: false,
    userCount: 12,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '3',
    name: 'Nurse',
    description: 'Can view and update patient records',
    isActive: true,
    isSystem: false,
    userCount: 8,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '4',
    name: 'Receptionist',
    description: 'Can manage appointments and patient check-ins',
    isActive: false,
    isSystem: false,
    userCount: 3,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

const cardStyles = {
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.25s ease',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    overflow: 'hidden',
    bgcolor: 'background.paper',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: 8,
      borderColor: 'primary.light',
    }
  },
  addCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    border: '2px dashed',
    borderColor: 'divider',
    borderRadius: 2,
    p: 3,
    bgcolor: 'background.paper',
    color: 'text.secondary',
    '&:hover': {
      borderColor: 'primary.main',
      bgcolor: 'action.hover',
      color: 'primary.main',
      '& .MuiSvgIcon-root': {
        color: 'primary.main',
      },
    }
  },
  cardHeader: {
    p: 2,
    '& .MuiCardHeader-content': { 
      minWidth: 0,
      overflow: 'hidden',
    },
    '& .MuiCardHeader-title': { 
      fontSize: '1.1rem',
      fontWeight: 600,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '& .MuiChip-root': { 
        ml: 1,
        verticalAlign: 'middle',
      },
    },
    '& .MuiCardHeader-subheader': { 
      mt: 0.5,
      color: 'text.secondary',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  cardContent: {
    p: 0,
    px: 2,
    pb: 1,
    flexGrow: 1,
  },
  cardFooter: {
    p: 1.5,
    px: 2,
    pb: 2,
    borderTop: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.default',
  },
  statusChip: {
    height: 20,
    fontSize: '0.65rem',
    fontWeight: 600,
    '&.MuiChip-colorSuccess': {
      bgcolor: 'success.light',
      color: 'success.dark',
    },
    '&.MuiChip-colorDefault': {
      bgcolor: 'action.selected',
      color: 'text.secondary',
    },
  }
};

const UserRoleList = () => {
  const [roles, setRoles] = useState<UIRole[]>(MOCK_ROLES as UIRole[]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [useLiveData, setUseLiveData] = useState<boolean>(false);
  const styles = cardStyles;

  // Fetch roles from API when useLiveData changes
  useEffect(() => {
    if (useLiveData) {
      fetchRoles();
    } else {
      // Reset to mock data when toggling off live data
      setRoles(MOCK_ROLES);
      setError(null);
    }
  }, [useLiveData]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use mock data if not using live data
      if (!useLiveData) {
        setRoles(MOCK_ROLES);
        return;
      }
      
      // Fetch from API
      const data = await roleApi.getRoles();
      
      // Transform the API response to match our UIRole type and filter out soft-deleted roles
      const formattedRoles = data
        .filter(role => !role.isDeleted) // Filter out soft-deleted roles
        .map(role => ({
          ...role,
          description: role.description || '',
          isSystem: role.isSystem || false,
          userCount: role.userCount || 0,
          isDeleted: role.isDeleted || false,
          createdAt: role.createdAt || new Date().toISOString(),
          updatedAt: role.updatedAt || new Date().toISOString()
        }));
      
      setRoles(formattedRoles);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      setError('Failed to load roles. Using mock data instead.');
      // Fallback to mock data
      setRoles(MOCK_ROLES);
      setUseLiveData(false);
    } finally {
      setLoading(false);
    }
  };

  const toggleDataMode = async () => {
    const newMode = !useLiveData;
    setUseLiveData(newMode);
    
    if (newMode) {
      try {
        setLoading(true);
        await fetchRoles();
      } catch (err) {
        console.error('Failed to fetch roles:', err);
        setError('Failed to load roles. Using mock data instead.');
        setUseLiveData(false);
      } finally {
        setLoading(false);
      }
    } else {
      // Reset to mock data
      setRoles(MOCK_ROLES);
      setError(null);
    }
  };

  const { enqueueSnackbar } = useSnackbar();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<{ id: string; name: string } | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [currentRole, setCurrentRole] = useState<UIRole | null>(null);

  const handleAddRole = () => {
    setIsAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
  };

  const handleCloseEditDialog = () => {
    setEditingRole(null);
  };

  const handleViewPermissions = async (role: Role) => {
    try {
      setCurrentRole(role);
      setPermissionsDialogOpen(true);
      
      // Fetch menu permissions for this role
      const permissions = await menuApi.getMenuPermissions();
      // TODO: Update permissions dialog with the fetched data
      console.log('Fetched permissions:', permissions);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      enqueueSnackbar('Failed to load permissions', { variant: 'error' });
    }
  };

  const handlePermissionsSave = async (permissions: any) => {
    if (!currentRole) return;
    
    try {
      // Call your API to update permissions
      // await roleApi.updateRolePermissions(currentRole.id, permissions);
      
      enqueueSnackbar('Permissions updated successfully', { variant: 'success' });
      setPermissionsDialogOpen(false);
      if (useLiveData) {
        fetchRoles(); // Refresh roles list if using live data
      }
    } catch (error) {
      console.error('Failed to update permissions:', error);
      enqueueSnackbar('Failed to update permissions', { variant: 'error' });
    }
  };

  const handleClosePermissionsDialog = () => {
    setPermissionsDialogOpen(false);
    setSelectedRole(null);
  };

  const handleSaveRole = async (roleData: { 
    name: string; 
    description: string; 
    status: 'active' | 'inactive' 
  }) => {
    try {
      // First, try to create the role
      const result = await roleApi.createRole({
        name: roleData.name,
        description: roleData.description || null,
        isActive: roleData.status === 'active',
        isSystem: false
      });
      
      // If successful, update the UI and return
      if (result.data) {
        setRoles(prevRoles => [...prevRoles, result.data!]);
        enqueueSnackbar('Role created successfully', { variant: 'success' });
        handleCloseAddDialog();
        return;
      }
      
      // If we get an error, check if it's a duplicate name error
      if (result.error?.includes('duplicate') || result.error?.includes('already exists')) {
        // Fetch all roles including deleted ones
        const allRoles = await roleApi.getRoles(undefined, true);
        const existingRole = allRoles.find(r => 
          r.name.toLowerCase() === roleData.name.toLowerCase()
        );

        if (existingRole) {
          if (existingRole.isDeleted) {
            // Ask for confirmation to restore
            const confirmRestore = window.confirm(
              `A role named "${roleData.name}" was previously deleted. Do you want to restore it?`
            );

            if (confirmRestore) {
              // Restore the soft-deleted role
              const restoredRole = await roleApi.restoreRole(existingRole.id);
              
              if (restoredRole.error) {
                throw new Error(restoredRole.error);
              }

              // Update the role with new data
              const updatedRole = await roleApi.updateRole(existingRole.id, {
                name: roleData.name,
                description: roleData.description || null,
                isActive: roleData.status === 'active',
                isDeleted: false
              });

              if (updatedRole.error) {
                throw new Error(updatedRole.error);
              }

              if (updatedRole.data) {
                // Update the local state with the restored role
                setRoles(prevRoles => [
                  ...prevRoles.filter(r => r.id !== existingRole.id),
                  updatedRole.data!
                ]);
                enqueueSnackbar('Role restored and updated successfully', { variant: 'success' });
                handleCloseAddDialog();
              }
            } else {
              enqueueSnackbar('Role creation cancelled', { variant: 'info' });
            }
            return;
          } else {
            // Role with same name already exists and is not deleted
            throw new Error(`A role with the name "${roleData.name}" already exists.`);
          }
        }
      }
      
      // If we get here, there was an unexpected error
      if (result.error) {
        throw new Error(result.error);
      }
      
    } catch (error) {
      console.error('Failed to create/restore role:', error);
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to create/restore role', 
        { variant: 'error' }
      );
    }
  };

  const handleEdit = (roleId: string) => {
    const roleToEdit = roles.find(role => role.id === roleId);
    if (roleToEdit) {
      setEditingRole(roleToEdit);
    }
  };

  const handleSaveRoleChanges = async (roleData: { 
    id: string;
    name: string; 
    description: string; 
    status: 'active' | 'inactive' 
  }) => {
    try {
      const role = roles.find(r => r.id === roleData.id);
      if (!role) {
        throw new Error('Role not found');
      }

      // Check if making role inactive with assigned users
      if (role.isActive && roleData.status === 'inactive' && role.userCount > 0) {
        const confirmMessage = `This role is assigned to ${role.userCount} user(s). Making it inactive will prevent these users from logging in. Do you want to continue?`;
        if (!window.confirm(confirmMessage)) {
          return; // User cancelled
        }
      }

      const result = await roleApi.updateRole(roleData.id, {
        name: roleData.name,
        description: roleData.description,
        isActive: roleData.status === 'active'
      });

      if (result.error) {
        throw new Error(result.error);
      }
      
      if (result.data) {
        setRoles(prevRoles => 
          prevRoles.map(r => r.id === roleData.id ? result.data! : r)
        );
        enqueueSnackbar('Role updated successfully', { variant: 'success' });
        setEditingRole(null);
      }
    } catch (error) {
      console.error('Failed to update role:', error);
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to update role',
        { variant: 'error' }
      );
    }
  };

  const handleDelete = async (id: string) => {
    const role = roles.find(r => r.id === id);
    if (!role) {
      enqueueSnackbar('Role not found', { variant: 'error' });
      return;
    }

    // Prevent deletion of system roles
    if (role.isSystem) {
      enqueueSnackbar('System roles cannot be deleted', { variant: 'error' });
      return;
    }

    // Check if role has any users assigned
    try {
      const usersResponse = await roleApi.getRoleUsers(id);
      
      if (usersResponse.count > 0) {
        enqueueSnackbar(
          `Cannot delete role "${role.name}" as it is assigned to ${usersResponse.count} user(s).`,
          { variant: 'error' }
        );
        return;
      }

      const confirmMessage = `Are you sure you want to delete the role "${role.name}"?\n\n` +
        'This action will mark the role as deleted and it will no longer be available for assignment.\n' +
        'Note: You can restore this role later by creating a new role with the same name.';

      if (window.confirm(confirmMessage)) {
        const result = await roleApi.deleteRole(id);
        
        if (result.success) {
          // Update local state to reflect soft delete
          setRoles(prevRoles => 
            prevRoles.map(r => 
              r.id === id ? { ...r, isActive: false, isDeleted: true } : r
            )
          );
          enqueueSnackbar('Role deleted successfully', { variant: 'success' });
        } else {
          throw new Error(result.message || 'Failed to delete role');
        }
      }
    } catch (error) {
      console.error('Failed to delete role:', error);
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to delete role',
        { variant: 'error' }
      );
    }
  };

  if (loading && roles.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ 
        py: 4, 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px'
      }}>
        <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
        <Typography variant="h6" color="text.secondary">
          {useLiveData ? 'Loading roles from server...' : 'Loading mock data...'}
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          User Roles
        </Typography>
        <Tooltip title={useLiveData ? 'Switch to mock data' : 'Switch to live data'}>
          <Button
            variant="outlined"
            color={useLiveData ? 'success' : 'primary'}
            onClick={toggleDataMode}
            startIcon={useLiveData ? <ViewIcon /> : <EditIcon />}
            size="small"
            sx={{ ml: 2 }}
          >
            {useLiveData ? 'Live Data' : 'Mock Data'}
          </Button>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Add Role Card */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Card sx={{ ...styles.addCard }} onClick={handleAddRole} elevation={0}>
            <AddIcon 
              sx={{ 
                fontSize: '2.5rem',
                color: 'text.secondary',
                transition: 'all 0.25s ease',
              }} 
            />
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mt: 1.5,
                fontWeight: 500,
                color: 'inherit',
                transition: 'all 0.25s ease',
              }}
            >
              Add New Role
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                mt: 0.5,
                color: 'inherit',
                opacity: 0.7,
                textAlign: 'center',
                maxWidth: '80%',
                transition: 'all 0.25s ease',
              }}
            >
              Click to create a new user role
            </Typography>
          </Card>
        </Grid>
        
        {/* Role Cards */}
        {roles.map((role) => (
          <Grid item key={role.id} xs={12} sm={6} md={4} lg={3}>
            <Card sx={styles.card}>
              <CardHeader
                avatar={
                  <Avatar 
                    sx={{ 
                      bgcolor: role.isActive ? 'primary.main' : 'grey.500',
                      color: 'white',
                      width: 40,
                      height: 40,
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      boxShadow: 1
                    }}
                  >
                    {role.name.charAt(0).toUpperCase()}
                  </Avatar>
                }
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h6" component="div" noWrap>
                      {role.name}
                      {role.isSystem && (
                        <Chip 
                          label="System" 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                          sx={{ 
                            ml: 1, 
                            fontSize: '0.65rem', 
                            height: 20,
                            fontWeight: 500,
                            borderWidth: '1px'
                          }} 
                        />
                      )}
                    </Typography>
                  </Box>
                }
                subheader={
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {role.description || 'No description provided'}
                  </Typography>
                }
                sx={styles.cardHeader}
              />
              
              <CardContent sx={styles.cardContent}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip 
                    icon={<GroupIcon fontSize="small" />}
                    label={`${role.userCount} ${role.userCount === 1 ? 'User' : 'Users'}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      '& .MuiChip-icon': { 
                        color: 'text.secondary',
                        marginLeft: '4px',
                      },
                      '& .MuiChip-label': { 
                        pl: 0.5,
                        pr: 1,
                        fontWeight: 500,
                        color: 'text.secondary'
                      },
                      height: 24,
                      borderRadius: '6px'
                    }}
                  />
                </Box>
                
                <Box sx={styles.cardFooter}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Updated: {new Date(role.updatedAt).toLocaleDateString()}
                    </Typography>
                    <Chip 
                      label={role.isActive ? 'Active' : 'Inactive'} 
                      size="small"
                      color={role.isActive ? 'success' : 'default'}
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        '&.MuiChip-colorSuccess': {
                          bgcolor: 'success.light',
                          color: 'success.dark',
                        },
                        '&.MuiChip-colorDefault': {
                          bgcolor: 'action.selected',
                          color: 'text.secondary',
                        },
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
              
              {!role.isSystem && (
                <>
                  <Divider />
                  <CardActions sx={{ 
                    p: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                    borderTop: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Permissions">
                        <IconButton 
                          size="small"
                          color="primary"
                          onClick={() => handleViewPermissions(role)}
                          sx={{
                            '&:hover': { bgcolor: 'primary.light', color: 'primary.contrastText' },
                            transition: 'all 0.2s',
                            p: 0.75
                          }}
                        >
                          <LockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(role.id);
                          }}
                          sx={{
                            '&:hover': { bgcolor: 'action.hover' },
                            transition: 'all 0.2s',
                            p: 0.75
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(role.id);
                        }}
                        sx={{
                          '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' },
                          transition: 'all 0.2s',
                          p: 0.75
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {/* Add Role Dialog */}
      <AddRoleDialog
        open={isAddDialogOpen}
        onClose={handleCloseAddDialog}
        onSave={handleSaveRole}
      />
      
      {currentRole && (
        <RolePermissionsDialog
          open={permissionsDialogOpen}
          onClose={handleClosePermissionsDialog}
          onSave={handlePermissionsSave}
          roleId={currentRole.id}
          roleName={currentRole.name}
          isSystem={currentRole.isSystem}
        />
      )}
      
      {editingRole && (
        <EditRoleDialog
          open={!!editingRole}
          onClose={handleCloseEditDialog}
          onSave={handleSaveRoleChanges}
          role={editingRole}
        />
      )}
    </Container>
  );
};

export default UserRoleList;
